import React, { useState, useEffect, useCallback } from 'react';
import KnktToast from '../components/KnktToast';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Monitor, Smartphone, Tag, Database } from 'lucide-react';
import templatesAPI from '../api/templates';
import BlockPaletteNew from '../components/TemplateEditor/BlockPaletteNew';
import TemplateEditor from '../components/TemplateEditor/TemplateEditor';
import EmailPreviewFrame from '../components/TemplateEditor/EmailPreviewFrame';
import BlockEditorPanel from '../components/BlockEditors/BlockEditorPanel';
import TagsPanel from '../components/TagsPanel/TagsPanel';
import UsedVariablesPanel from '../components/UsedVariablesPanel/UsedVariablesPanel';
import { TagInsertProvider } from '../contexts/TagInsertContext';
import { getSocialLogo } from '../utils/socialLogos';
import '../styles/EditorPage.css';

const EditorPage = () => {
  const { id: urlId } = useParams();
  const navigate = useNavigate();

  // Handle edge case where ID is literally "undefined"
  const id = urlId === 'undefined' ? null : urlId;

  // If URL has "undefined", redirect to /editor
  React.useEffect(() => {
    if (urlId === 'undefined') {
      navigate('/editor', { replace: true });
    }
  }, [urlId, navigate]);

  // Template state
  const [template, setTemplate] = useState({
    name: 'Untitled Template',
    description: '',
    category: 'promo',
    blocks: [],
    settings: {
      emailWidth: 600,
      backgroundColor: '#0a0a0a',
      primaryColor: '#FF1493',
      fontFamily: 'Arial, sans-serif',
    },
  });

  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [previewHTML, setPreviewHTML] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null); // { message, subtext, type }
  const showToast = useCallback((message, subtext, type = 'success') => {
    setToast({ message, subtext, type });
  }, []);
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop | mobile
  const [sectionsExpanded, setSectionsExpanded] = useState({
    blocks: true,
    structure: true,
    settings: true,
    globalSettings: false,
    usedVariables: true, // Show used variables by default
    tags: true,  // Show tags panel by default
  });

  // Load template if editing existing
  useEffect(() => {
    if (id) {
      loadTemplate(id);
    } else {
      // Create default header and footer blocks for new templates
      initializeDefaultBlocks();
    }
  }, [id]);

  const loadTemplate = async (templateId) => {
    setLoading(true);
    try {
      const response = await templatesAPI.getById(templateId);

      // Platform API returns { success: true, template: {...} }
      const templateData = response.template || response.data?.template || response.data;

      const loadedTemplate = {
        ...templateData,
        blocks: templateData.blocks || [],
        settings: templateData.settings || template.settings,
      };

      setTemplate(loadedTemplate);
      updatePreview(loadedTemplate);
    } catch (error) {
      console.error('Failed to load template:', error);
      alert('Failed to load template: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaultBlocks = () => {
    const defaultBlocks = [
      {
        id: 'header-' + Date.now(),
        type: 'header',
        position: 0,
        config: {
          logo: {
            url: '',
            width: 120,
            height: 40,
          },
          navigationLinks: [],
        },
        styles: {
          backgroundColor: '#1a1a1a',
          padding: {
            top: 20,
            right: 20,
            bottom: 20,
            left: 20,
          },
        },
      },
      {
        id: 'footer-' + Date.now(),
        type: 'footer',
        position: 100,
        config: {
          companyName: 'KNKT',
          address: '',
          showUnsubscribe: true,
          socialLinks: [],
        },
        styles: {
          backgroundColor: '#1a1a1a',
          padding: {
            top: 20,
            right: 20,
            bottom: 20,
            left: 20,
          },
          textAlign: 'center',
        },
      },
    ];

    const updatedTemplate = {
      ...template,
      blocks: defaultBlocks,
    };

    setTemplate(updatedTemplate);
    updatePreview(updatedTemplate);
  };

  const updatePreview = (templateData) => {
    // Generate simple client-side preview without backend call
    const html = generatePreviewHTML(templateData);
    setPreviewHTML(html);
  };

  const generatePreviewHTML = (templateData) => {
    const { blocks = [], settings = {} } = templateData;

    const getPaddingStyle = (padding) => {
      if (typeof padding === 'object' && padding !== null) {
        return `${padding.top || 0}px ${padding.right || 0}px ${padding.bottom || 0}px ${padding.left || 0}px`;
      }
      return padding || '20px';
    };

    const blockHTML = (blocks || [])
      .sort((a, b) => a.position - b.position)
      .map((block) => {
        const paddingStyle = getPaddingStyle(block.styles.padding);

        switch (block.type) {
          case 'header':
            const logoUrl = block.config.logo?.url || '';
            const navLinks = block.config.navigationLinks || [];
            const logoHTML = logoUrl ? `<img src="${logoUrl}" alt="Logo" style="max-height: ${block.config.logo?.height || 40}px; max-width: ${block.config.logo?.width || 120}px; height: auto;" />` : '<span style="color: #666; font-size: 14px;">Add logo in editor →</span>';
            const navHTML = navLinks.length > 0 ? navLinks.map(link => `<a href="${link.url}" style="color: #00D9FF; text-decoration: none; margin-left: 15px; font-size: 14px;">${link.label}</a>`).join('') : '';
            return `<div style="background: ${block.styles.backgroundColor}; padding: ${paddingStyle}; display: flex; align-items: center; justify-content: space-between;">
              <div>${logoHTML}</div>
              <div>${navHTML}</div>
            </div>`;
          case 'hero':
            const heroTextColor = block.styles.color || '#ffffff';
            return `<div style="background: ${block.styles.backgroundColor}; padding: ${paddingStyle}; text-align: ${block.config.textAlign || 'center'};">
              ${block.config.imageUrl ? `<img src="${block.config.imageUrl}" alt="Hero" style="max-width: 100%; height: auto; border-radius: 8px; margin-bottom: 20px;" />` : ''}
              <h1 style="margin: 20px 0 10px; color: ${heroTextColor}; font-size: 36px; font-weight: bold;">${block.config.headline || 'Your Headline Here'}</h1>
              <p style="margin: 0; color: ${heroTextColor}; opacity: 0.8; font-size: 18px;">${block.config.subheadline || 'Your subheadline text'}</p>
            </div>`;
          case 'image':
            const imageAlignment = block.config.alignment || 'center';
            const imageMaxWidth = block.config.maxWidth || 600;
            const imageUrl = block.config.imageUrl;
            const imageAlt = block.config.imageAlt || '';
            const imageLinkUrl = block.config.linkUrl;

            if (!imageUrl) {
              return `<div style="background: ${block.styles.backgroundColor}; padding: ${paddingStyle}; text-align: ${imageAlignment}; color: #999;">
                <p>No image selected</p>
              </div>`;
            }

            const imageElement = `<img src="${imageUrl}" alt="${imageAlt}" style="max-width: ${imageMaxWidth}px; width: 100%; height: auto; display: block; margin: 0 auto;" />`;
            const imageContent = imageLinkUrl
              ? `<a href="${imageLinkUrl}" style="display: inline-block; text-decoration: none;">${imageElement}</a>`
              : imageElement;

            return `<div style="background: ${block.styles.backgroundColor}; padding: ${paddingStyle}; text-align: ${imageAlignment};">
              ${imageContent}
            </div>`;
          case 'content':
            const textColor = block.styles.color || '#ffffff';
            const fontSize = block.styles.fontSize || 16;
            const textAlign = block.styles.textAlign || 'left';
            const contentBody = block.config.body || '<p style="color: #ffffff;">Content goes here...</p>';

            return `<div style="padding: ${paddingStyle}; background: ${block.styles.backgroundColor || 'transparent'}; color: ${textColor}; font-size: ${fontSize}px; text-align: ${textAlign}; line-height: 1.6;">
              ${contentBody}
            </div>`;
          case 'tracklist':
            return `<div style="padding: ${paddingStyle}; background: ${block.styles.backgroundColor};">
              <h3 style="color: #fff; margin: 0 0 15px;">Tracklist</h3>
              <div style="color: #ccc;">
                <div style="padding: 8px; border-bottom: 1px solid #333;">Track 1 - 3:45</div>
                <div style="padding: 8px; border-bottom: 1px solid #333;">Track 2 - 4:12</div>
                <div style="padding: 8px;">Track 3 - 3:30</div>
              </div>
            </div>`;
          case 'music-player':
            const playerTitle = block.config.title || 'Music Player';
            const tracks = block.config.tracks || [];
            const primaryColor = block.config.primaryColor || '#00D9FF';
            const playerTextColor = block.config.textColor || '#ffffff';
            const artworkUrl = block.config.artworkUrl || '';
            const showArtwork = block.config.showArtwork !== false;

            // Show Spotify links or custom player tracks
            const trackListHTML = tracks.length > 0 ? tracks.map((track, idx) => {

              if (track.spotifyUrl) {
                // Extract track ID from Spotify URL for embed
                const trackIdMatch = track.spotifyUrl.match(/track\/([a-zA-Z0-9]+)/);
                const trackId = trackIdMatch ? trackIdMatch[1] : null;

                const spotifyLogo = `<svg viewBox="0 0 24 24" width="20" height="20" fill="#1DB954" xmlns="http://www.w3.org/2000/svg"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>`;

                return `
                  <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom: 16px; background: rgba(29, 185, 84, 0.1); border-radius: 8px; overflow: hidden;">
                    <tr>
                      <td style="padding: 16px;">
                        ${trackId ? `
                          <!-- Spotify Embed (shows in preview, stripped by email clients) -->
                          <div style="margin-bottom: 16px;">
                            <iframe
                              style="border-radius: 12px; width: 100%; height: 152px;"
                              src="https://open.spotify.com/embed/track/${trackId}?utm_source=generator&theme=0"
                              frameBorder="0"
                              allowfullscreen=""
                              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                              loading="lazy">
                            </iframe>
                          </div>
                        ` : ''}

                        <!-- Email-safe fallback button (shows in actual emails) -->
                        <table width="100%" cellpadding="0" cellspacing="0">
                          <tr>
                            <td style="vertical-align: middle;">
                              ${spotifyLogo}
                            </td>
                            <td style="padding-left: 12px; vertical-align: middle;">
                              <div style="color: ${playerTextColor}; font-weight: 600; font-size: 15px; margin-bottom: 4px;">
                                ${track.title || 'Untitled Track'}
                              </div>
                              <div style="color: ${playerTextColor}; opacity: 0.7; font-size: 13px;">
                                ${track.artist || 'Unknown Artist'}
                              </div>
                            </td>
                            <td style="text-align: right; vertical-align: middle; padding-left: 16px;">
                              <a href="${track.spotifyUrl}" target="_blank" style="display: inline-block; background: #1DB954; color: #ffffff; text-decoration: none; padding: 8px 20px; border-radius: 20px; font-weight: 600; font-size: 13px;">
                                Play on Spotify
                              </a>
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>
                `;
              } else {
                // Render custom player interface
                return `
                  <table width="100%" cellpadding="0" cellspacing="0" style="border-bottom: 1px solid rgba(255,255,255,0.1); margin-bottom: 8px;">
                    <tr>
                      <td style="padding: 12px 0; width: 40px; vertical-align: middle;">
                        <table cellpadding="0" cellspacing="0" style="width: 32px; height: 32px; border-radius: 16px; background-color: ${primaryColor};">
                          <tr>
                            <td align="center" valign="middle" style="color: #000000; font-size: 14px; font-weight: bold; padding-left: 2px;">▶</td>
                          </tr>
                        </table>
                      </td>
                      <td style="padding: 12px 8px; color: ${playerTextColor}; vertical-align: middle;">
                        <div style="font-weight: 600; margin-bottom: 4px; line-height: 1.2;">${track.title || 'Untitled Track'}</div>
                        <div style="font-size: 12px; color: ${playerTextColor}; opacity: 0.7; line-height: 1.2;">${track.artist || 'Unknown Artist'}</div>
                      </td>
                      <td style="padding: 12px 0; text-align: right; color: ${playerTextColor}; font-size: 12px; width: 60px; vertical-align: middle;">
                        ${track.duration || '0:00'}
                      </td>
                    </tr>
                  </table>
                `;
              }
            }).join('') : `
              <table width="100%" cellpadding="0" cellspacing="0" style="background: rgba(0,0,0,0.2); border-radius: 8px; padding: 24px;">
                <tr>
                  <td align="center" valign="middle">
                    <div style="color: ${playerTextColor}; opacity: 0.6; font-size: 14px; line-height: 1.6;">
                      <div style="font-size: 36px; margin-bottom: 8px;">🎵</div>
                      <div style="font-weight: 600;">No tracks yet</div>
                      <div style="font-size: 12px; margin-top: 4px;">Add tracks in the editor to preview</div>
                    </div>
                  </td>
                </tr>
              </table>
            `;

            return `<div style="padding: ${paddingStyle}; background: ${block.styles.backgroundColor};">
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  ${showArtwork && artworkUrl ? `
                    <td style="width: 120px; padding-right: 20px; vertical-align: top;">
                      <img src="${artworkUrl}" alt="Artwork" style="width: 120px; height: 120px; border-radius: 8px; display: block;" />
                    </td>
                  ` : ''}
                  <td style="vertical-align: top;">
                    <h3 style="color: ${playerTextColor}; margin: 0 0 16px; font-size: 18px; font-weight: 600;">${playerTitle}</h3>
                    ${trackListHTML}
                  </td>
                </tr>
              </table>
            </div>`;
          case 'cta':
            const buttonColor = block.config.buttonColor || '#FF1493';
            const buttonTextColor = block.config.buttonTextColor || '#ffffff';
            const borderRadius = block.config.borderRadius || 8;
            const buttonAlign = block.styles.textAlign || 'center';

            return `<div style="padding: ${paddingStyle}; text-align: ${buttonAlign}; background: ${block.styles.backgroundColor};">
              <a href="${block.config.primaryUrl || '#'}" style="display: inline-block; background: ${buttonColor}; color: ${buttonTextColor}; padding: 12px 30px; text-decoration: none; border-radius: ${borderRadius}px; font-weight: bold;">
                ${block.config.primaryText || 'Click Here'}
              </a>
            </div>`;
          case 'social':
            const platforms = block.config.platforms || [];
            const iconSize = block.config.iconSize || 32;
            const socialIcons = platforms.map(platform => {
              const logo = getSocialLogo(platform.type, iconSize);
              return `<a href="${platform.url || '#'}" style="display: inline-block; margin: 0 8px;" title="${platform.label || platform.type}">${logo}</a>`;
            }).join('');

            return `<div style="padding: ${paddingStyle}; text-align: center; background: ${block.styles.backgroundColor};">
              ${socialIcons || '<p style="color: #999; margin: 0;">Add social media links in the editor</p>'}
            </div>`;
          case 'footer':
            const footerSocialLinks = (block.config.socialLinks || []).map(link => {
              const footerLogo = getSocialLogo(link.platform, 24);
              return `<a href="${link.url || '#'}" style="display: inline-block; margin: 0 6px;" title="${link.label || link.platform}">${footerLogo}</a>`;
            }).join('');

            return `<div style="background: ${block.styles.backgroundColor}; padding: ${paddingStyle}; text-align: ${block.styles.textAlign}; color: #999; font-size: 12px;">
              ${block.config.companyName ? `<p style="margin: 0 0 8px;">${block.config.companyName}</p>` : ''}
              ${block.config.address ? `<p style="margin: 0 0 8px;">${block.config.address}</p>` : ''}
              ${footerSocialLinks ? `<div style="margin: 12px 0;">${footerSocialLinks}</div>` : ''}
              ${block.config.showUnsubscribe ? '<p style="margin: 0;"><a href="#" style="color: #999;">Unsubscribe</a></p>' : ''}
            </div>`;
          case 'custom':
            return `<div style="padding: ${paddingStyle}; background: ${block.styles.backgroundColor};">
              ${block.config.html || '<p style="color: #999;">Custom HTML</p>'}
            </div>`;
          case 'spacer':
            return `<div style="height: ${block.config.height || 40}px;"></div>`;
          case 'divider':
            return `<hr style="border: none; border-top: ${block.config.thickness || 1}px ${block.config.style || 'solid'} ${block.config.color || '#333'}; margin: 0;" />`;
          case 'video':
            const extractYouTubeId = (url) => {
              if (!url) return '';
              const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
              const match = url.match(regExp);
              return (match && match[2].length === 11) ? match[2] : '';
            };
            const videoId = extractYouTubeId(block.config.videoUrl);
            const videoTextColor = block.styles.color || '#ffffff';

            return `<div style="padding: ${paddingStyle}; background: ${block.styles.backgroundColor}; text-align: center;">
              ${block.config.title ? `<h3 style="color: ${videoTextColor}; margin: 0 0 10px; font-size: 20px;">${block.config.title}</h3>` : ''}
              ${block.config.description ? `<p style="color: ${videoTextColor}; margin: 0 0 20px; font-size: 14px;">${block.config.description}</p>` : ''}
              ${videoId ? `
                <div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; background: #000; border-radius: 8px;">
                  <iframe
                    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
                    src="https://www.youtube.com/embed/${videoId}${block.config.autoplay ? '?autoplay=1' : ''}"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                  ></iframe>
                </div>
              ` : '<div style="background: rgba(255,255,255,0.1); padding: 40px; border-radius: 8px;"><p style="color: #999; margin: 0;">Add a YouTube URL to preview the video</p></div>'}
            </div>`;
          default:
            return '';
        }
      })
      .join('');

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
        </head>
        <body style="margin: 0; padding: 0; background-color: #000000; font-family: ${settings.fontFamily};">
          <div style="max-width: ${settings.emailWidth}px; margin: 0 auto; background-color: ${settings.backgroundColor};">
            ${blockHTML}
          </div>
        </body>
      </html>
    `;
  };

  const handleAddBlock = (blockType) => {
    const newBlock = {
      id: `${blockType}-${Date.now()}`,
      type: blockType,
      position: template.blocks.length * 10,
      config: getDefaultBlockConfig(blockType),
      styles: getDefaultBlockStyles(blockType),
    };

    const updatedTemplate = {
      ...template,
      blocks: [...template.blocks, newBlock].sort((a, b) => a.position - b.position),
    };

    setTemplate(updatedTemplate);
    setSelectedBlockId(newBlock.id);
    updatePreview(updatedTemplate);
  };

  const handleUpdateBlock = (blockId, updates) => {
    const updatedBlocks = template.blocks.map((block) =>
      block.id === blockId ? { ...block, ...updates } : block
    );

    const updatedTemplate = {
      ...template,
      blocks: updatedBlocks,
    };

    setTemplate(updatedTemplate);
    updatePreview(updatedTemplate);
  };

  const handleDeleteBlock = (blockId) => {
    const updatedTemplate = {
      ...template,
      blocks: template.blocks.filter((block) => block.id !== blockId),
    };

    setTemplate(updatedTemplate);
    setSelectedBlockId(null);
    updatePreview(updatedTemplate);
  };

  const handleReorderBlocks = (reorderedBlocks) => {
    const updatedTemplate = {
      ...template,
      blocks: reorderedBlocks,
    };

    setTemplate(updatedTemplate);
    updatePreview(updatedTemplate);
  };

  const handleSave = async () => {
    if (!template.name || template.name.trim() === '') {
      alert('Please enter a template name');
      return;
    }

    setSaving(true);
    try {
      let response;

      // Prepare template data with useCase
      const templateData = {
        ...template,
        useCase: template.useCase || 'promo', // Default to promo if not set
      };

      if (id) {
        console.log('Updating template:', id, templateData);
        response = await templatesAPI.update(id, templateData);
      } else {
        console.log('Creating new template:', templateData);
        response = await templatesAPI.create(templateData);
        console.log('Template created:', response.data);

        // Navigate to edit page after creating
        const templateId = response.data?.template?._id || response.data?._id;
        if (templateId) {
          navigate(`/editor/${templateId}`, { replace: true });
        }
      }
      showToast('Template saved', null, 'success');
    } catch (error) {
      console.error('Save error:', error);
      showToast('Save failed', error.response?.data?.message || error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Removed handlePublish - auto-publish happens on save now

  const handleTestEmail = async () => {
    if (!id) {
      showToast('Save the template first', null, 'error');
      return;
    }

    const testEmail = prompt('Enter email address to send test:');
    if (!testEmail) return;

    try {
      await templatesAPI.test(id, testEmail, {
        contact: { name: 'Test User', email: testEmail },
        release: { title: 'Test Release', artist: 'Test Artist' },
      });
      showToast('Test email sent', testEmail, 'success');
    } catch (error) {
      console.error('Test email error:', error);
      showToast('Send failed', error.message, 'error');
    }
  };

  const toggleSection = (section) => {
    setSectionsExpanded((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Loading template...</div>
      </div>
    );
  }

  return (
    <TagInsertProvider>
    <div className="editor-page">
      {toast && <KnktToast message={toast.message} subtext={toast.subtext} type={toast.type} onClose={() => setToast(null)} />}
      {/* Editor Header */}
      <div className="editor-header">
        <div className="editor-header-left">
          <button className="btn-back" onClick={() => navigate('/')}>
            ← Back
          </button>
          <input
            type="text"
            className="template-name-input"
            value={template.name}
            onChange={(e) => setTemplate({ ...template, name: e.target.value })}
            placeholder="Template Name"
          />
        </div>

        <div className="editor-header-right">
          <button className="btn-secondary" onClick={handleTestEmail} disabled={!id}>
            Send Test
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Editor Main Content - 2 Panel Layout */}
      <div className="editor-content">
        {/* Left Panel: Blocks + Structure + Settings (All in One) */}
        <div className="editor-left-panel">
          <div className="left-panel-scroll">
            {/* Section 1: Block Palette */}
            <div className="editor-section">
              <button
                className="section-header"
                onClick={() => toggleSection('blocks')}
              >
                <span className="section-title">Add Blocks</span>
                {sectionsExpanded.blocks ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              {sectionsExpanded.blocks && (
                <div className="section-content">
                  <BlockPaletteNew onAddBlock={handleAddBlock} />
                </div>
              )}
            </div>

            {/* Section 2: Template Structure */}
            <div className="editor-section">
              <button
                className="section-header"
                onClick={() => toggleSection('structure')}
              >
                <span className="section-title">Template Structure</span>
                {sectionsExpanded.structure ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              {sectionsExpanded.structure && (
                <div className="section-content">
                  <TemplateEditor
                    blocks={template.blocks}
                    selectedBlockId={selectedBlockId}
                    onSelectBlock={setSelectedBlockId}
                    onUpdateBlock={handleUpdateBlock}
                    onDeleteBlock={handleDeleteBlock}
                    onReorderBlocks={handleReorderBlocks}
                    onAddBlock={handleAddBlock}
                  />
                </div>
              )}
            </div>

            {/* Section 3: Block Settings (when block selected) */}
            {selectedBlockId && (
              <div className="editor-section">
                <button
                  className="section-header"
                  onClick={() => toggleSection('settings')}
                >
                  <span className="section-title">Block Settings</span>
                  {sectionsExpanded.settings ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {sectionsExpanded.settings && (
                  <div className="section-content">
                    <BlockEditorPanel
                      selectedBlock={(template.blocks || []).find(b => b.id === selectedBlockId)}
                      onUpdateBlock={handleUpdateBlock}
                      onClose={() => setSelectedBlockId(null)}
                      isInline={true}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Global Template Settings */}
            <div className="editor-section">
              <button
                className="section-header"
                onClick={() => toggleSection('globalSettings')}
              >
                <span className="section-title">Global Settings</span>
                {sectionsExpanded.globalSettings ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              {sectionsExpanded.globalSettings && (
                <div className="section-content">
                  <div className="template-settings">
                    <div className="settings-group">
                      <label>Email Width (px)</label>
                      <input
                        type="number"
                        value={template.settings.emailWidth}
                        onChange={(e) =>
                          setTemplate({
                            ...template,
                            settings: { ...template.settings, emailWidth: parseInt(e.target.value) },
                          })
                        }
                      />
                    </div>
                    <div className="settings-group">
                      <label>Background Color</label>
                      <input
                        type="color"
                        value={template.settings.backgroundColor}
                        onChange={(e) =>
                          setTemplate({
                            ...template,
                            settings: { ...template.settings, backgroundColor: e.target.value },
                          })
                        }
                      />
                    </div>
                    <div className="settings-group">
                      <label>Primary Color</label>
                      <input
                        type="color"
                        value={template.settings.primaryColor}
                        onChange={(e) =>
                          setTemplate({
                            ...template,
                            settings: { ...template.settings, primaryColor: e.target.value },
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Used Variables Section - Shows what variables are in the template */}
            <div className="editor-section">
              <button
                className="section-header"
                onClick={() => toggleSection('usedVariables')}
              >
                <span className="section-title">
                  <Database size={14} style={{ marginRight: '8px' }} />
                  Used Variables ({template.blocks ? (() => {
                    const regex = /\{\{([^}]+)\}\}/g;
                    const vars = new Set();
                    template.blocks.forEach(b => {
                      const fields = [b.config?.body, b.config?.headline, b.config?.subheadline, b.config?.title, b.config?.description, b.config?.primaryText, b.config?.html];
                      fields.forEach(f => {
                        if (typeof f === 'string') {
                          let m;
                          while ((m = regex.exec(f)) !== null) vars.add(m[1].split('|')[0].trim());
                        }
                      });
                    });
                    return vars.size;
                  })() : 0})
                </span>
                {sectionsExpanded.usedVariables ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              {sectionsExpanded.usedVariables && (
                <div className="section-content">
                  <UsedVariablesPanel blocks={template.blocks} />
                </div>
              )}
            </div>

            {/* Section: Dynamic Tags/Variables */}
            <div className="editor-section">
              <button
                className="section-header"
                onClick={() => toggleSection('tags')}
              >
                <span className="section-title">
                  <Tag size={14} style={{ marginRight: '8px' }} />
                  Dynamic Tags
                </span>
                {sectionsExpanded.tags ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              {sectionsExpanded.tags && (
                <div className="section-content tags-section-content">
                  <TagsPanel />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel: Preview Only */}
        <div className="editor-right-panel">
          <div className="preview-header">
            <h3>Live Preview</h3>
            <div className="preview-controls">
              <div className="preview-mode-toggle">
                <button
                  className={`mode-btn ${previewMode === 'desktop' ? 'active' : ''}`}
                  onClick={() => setPreviewMode('desktop')}
                  title="Desktop Preview"
                >
                  <Monitor size={16} />
                </button>
                <button
                  className={`mode-btn ${previewMode === 'mobile' ? 'active' : ''}`}
                  onClick={() => setPreviewMode('mobile')}
                  title="Mobile Preview"
                >
                  <Smartphone size={16} />
                </button>
              </div>
              <button
                className="btn-secondary btn-sm"
                onClick={() => updatePreview(template)}
              >
                Refresh
              </button>
            </div>
          </div>
          <div className={`preview-container ${previewMode}`}>
            <EmailPreviewFrame html={previewHTML} mode={previewMode} />
          </div>
        </div>
      </div>
    </div>
    </TagInsertProvider>
  );
};

// Helper functions for default block configurations
const getDefaultBlockConfig = (blockType) => {
  const defaults = {
    header: {
      logo: {
        url: '',
        width: 120,
        height: 40,
      },
      navigationLinks: [],
    },
    hero: {
      imageUrl: 'https://via.placeholder.com/600x300',
      headline: 'Your Headline Here',
      subheadline: 'Your subheadline text',
      textAlign: 'center',
    },
    image: {
      imageUrl: '',
      imageAlt: '',
      imageWidth: null,
      imageHeight: null,
      linkUrl: '',
      alignment: 'center',
      maxWidth: 600,
    },
    content: {
      body: '<p>Add your content here...</p>',
    },
    tracklist: {
      showArtwork: true,
      showDuration: true,
      showDownloadLinks: true,
    },
    'music-player': {
      title: 'Listen to Preview',
      artworkUrl: '',
      showArtwork: true,
      tracks: [],
      primaryColor: '#00D9FF',
      textColor: '#ffffff',
    },
    cta: {
      primaryText: 'Primary Button',
      primaryUrl: '#',
      secondaryText: '',
      secondaryUrl: '',
    },
    social: {
      platforms: [],
    },
    footer: {
      companyName: 'KNKT',
      address: '',
      showUnsubscribe: true,
      socialLinks: [],
    },
    custom: {
      html: '<p>Custom HTML here...</p>',
    },
    spacer: {
      height: 40,
    },
    divider: {
      style: 'solid',
      color: '#333333',
      thickness: 1,
    },
    video: {
      videoUrl: '',
      title: '',
      description: '',
      autoplay: false,
    },
  };

  return defaults[blockType] || {};
};

const getDefaultBlockStyles = (blockType) => {
  // Video and hero blocks get black background by default
  const needsBlackBg = ['hero', 'video'].includes(blockType);

  return {
    backgroundColor: needsBlackBg ? '#000000' : 'transparent',
    color: '#ffffff', // White text by default for visibility on dark backgrounds
    fontSize: 16,
    padding: {
      top: 20,
      right: 20,
      bottom: 20,
      left: 20,
    },
    textAlign: blockType === 'hero' || blockType === 'footer' ? 'center' : 'left',
  };
};

export default EditorPage;
